import React, { useState, useEffect, useContext } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import LocContext from "../context/locContext";
import {
  fetchInvestorVerificationLink,
  formatVerificationLinkError,
  openVerificationLinkSuccess,
} from "../../utils/bseKycLink";
import {
    FiUser,
    FiMail,
    FiPhone,
    FiCalendar,
    FiMapPin,
    FiFileText,
    FiCheckCircle,
    FiXCircle,
    FiAlertCircle,
} from "react-icons/fi";

// Format date
const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

// Validation helpers (same as Profile.jsx)
const textOnlyRegex = /^[A-Za-z\s]+$/;
const numberOnlyRegex = /^[0-9]+$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const is18Plus = (dob) => {
    if (!dob) return false;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age >= 18;
};

// Map backend ucc to KYC status
const getKycStatusFromUcc = (ucc) => {
    if (!ucc) return "pending";
    if (ucc === "Reject") return "rejected";
    return "approved";
};

export default function ProfileKYC() {
    const { userData } = useContext(LocContext);
    const id = userData?.id;

    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showRejectionReason, setShowRejectionReason] = useState(false);
    const [loading, setLoading] = useState(true);
    const [kycLinkLoading, setKycLinkLoading] = useState(false);

    // Raw backend data (user + investment merged)
    const [userDataP, setUserDataP] = useState({});

    // Profile form state (camelCase for UI)
    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dateOfBirth: "",
        pan: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
    });

    // KYC state derived from userDataP.ucc
    const kycStatus = getKycStatusFromUcc(userDataP?.ucc);
    const [kycRejectionReason] = useState("PAN card image is not clear. Please upload a clear, high-resolution image of your PAN card.");

    // KYC documents (placeholder - backend user_doc API can be wired later)
    const [kycDocuments, setKycDocuments] = useState([
        { id: 1, type: "PAN Card", file: null, status: "pending", uploadedAt: null },
        { id: 2, type: "Aadhaar Card", file: null, status: "pending", uploadedAt: null },
        { id: 3, type: "Address Proof", file: null, status: "pending", uploadedAt: null },
    ]);

    // ---------- Fetch Profile Data (same as Profile.jsx) ----------
    const getProfileData = async () => {
        if (!id) return;
        try {
            const [resUser, resInvestment] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/${id}`),
                axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/investment/user/${id}`),
            ]);

            if (resUser?.status === 200) {
                const merged = {
                    ...resUser.data?.data,
                    ...(resInvestment?.data?.data || {}),
                };
                setUserDataP(merged);

                // Map backend fields to profileData
                const addr1 = merged.address_line1 || "";
                const addr2 = merged.address_line2 || "";
                const fullAddress = addr2 ? `${addr1}\n${addr2}` : addr1;

                setProfileData({
                    firstName: merged.first_name || "",
                    lastName: merged.last_name || "",
                    email: merged.email || "",
                    phone: merged.phone || "",
                    dateOfBirth: merged.date_of_birth?.split("T")[0] || "",
                    pan: merged.pan_number || "",
                    address: fullAddress,
                    city: merged.city || "",
                    state: merged.state || "",
                    pincode: merged.pin_code || "",
                });
            }
        } catch (err) {
            console.error("Profile fetch error:", err);
            Swal.fire({ icon: "error", title: "Failed to load profile" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getProfileData();
    }, [id]);

    // ---------- KYC Status Config ----------
    const getKYCStatusConfig = (status) => {
        const configs = {
            pending: {
                color: "#006e82",
                bg: "#c8eeecbf",
                icon: FiAlertCircle,
                label: "Pending",
            },
            in_review: {
                color: "#3b82f6",
                bg: "#dbeafe",
                icon: FiAlertCircle,
                label: "In Review",
            },
            approved: {
                color: "#10b981",
                bg: "#d1fae5",
                icon: FiCheckCircle,
                label: "Approved",
            },
            rejected: {
                color: "#ef4444",
                bg: "#fee2e2",
                icon: FiXCircle,
                label: "Rejected",
            },
        };
        return configs[status] || configs.pending;
    };

    // ---------- Input Handlers (with validation like Profile.jsx) ----------
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Text-only fields
        if (["firstName", "lastName"].includes(name)) {
            if (value && !textOnlyRegex.test(value)) return;
        }

        // Number-only fields
        if (name === "pincode") {
            if (value && !numberOnlyRegex.test(value)) return;
        }

        // PAN - uppercase
        if (name === "pan") {
            setProfileData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }

        setProfileData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "");
        if (digits.length > 10) return;
        setProfileData((prev) => ({ ...prev, phone: digits }));
    };

    // ---------- File Upload (placeholder - wire to backend user_doc API when available) ----------
    const handleFileUpload = (e, docType) => {
        const file = e.target.files[0];
        if (file) {
            setKycDocuments((prev) =>
                prev.map((doc) =>
                    doc.type === docType
                        ? {
                              ...doc,
                              file: file.name,
                              status: "uploaded",
                              uploadedAt: new Date().toISOString(),
                          }
                        : doc
                )
            );
            Swal.fire({ icon: "success", title: `${docType} uploaded`, timer: 1500 });
        }
    };

    const resolveClientCode = () =>
        String(
            userDataP.app_code ||
                userDataP.client_code ||
                userData?.ucc ||
                ""
        ).trim();

    const handleStartKYC = async () => {
        const clientCode = resolveClientCode();
        if (!clientCode) {
            Swal.fire({
                icon: "warning",
                title: "Client code required",
                html: "Complete <strong>investor onboarding</strong> first so we have your BSE client code, or contact support.",
                confirmButtonText: "Go to onboarding",
            }).then((r) => {
                if (r.isConfirmed) window.location.href = "/investor-onboarding";
            });
            return;
        }

        setKycLinkLoading(true);
        try {
            const result = await fetchInvestorVerificationLink(clientCode);
            const { title, html, isInfo } = openVerificationLinkSuccess(result);
            Swal.fire({ icon: isInfo ? "info" : "success", title, html });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Verification link unavailable",
                html: formatVerificationLinkError(err),
            });
        } finally {
            setKycLinkLoading(false);
        }
    };

    // ---------- Save Profile (same API as Profile.jsx) ----------
    const handleSaveProfile = async (e) => {
        e.preventDefault();
        if (!id) return;

        // Validation
        if (!profileData.firstName?.trim()) {
            Swal.fire({ icon: "warning", title: "First name is required" });
            return;
        }
        if (!panRegex.test(profileData.pan)) {
            Swal.fire({ icon: "warning", title: "Invalid PAN format", text: "Format: ABCDE1234F" });
            return;
        }
        if (!is18Plus(profileData.dateOfBirth)) {
            Swal.fire({ icon: "warning", title: "Age must be 18 years or above" });
            return;
        }
        if (profileData.phone?.length !== 10) {
            Swal.fire({ icon: "warning", title: "Mobile number must be 10 digits" });
            return;
        }

        const addrParts = (profileData.address || "").split("\n");
        const address_line1 = addrParts[0] || "";
        const address_line2 = addrParts.slice(1).join("\n") || "";

        const payload = {
            ...userDataP,
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            email: profileData.email,
            phone: profileData.phone,
            country_code: userDataP.country_code || "+ 91",
            date_of_birth: profileData.dateOfBirth,
            pan_number: profileData.pan,
            address_line1,
            address_line2,
            city: profileData.city,
            state: profileData.state,
            pin_code: profileData.pincode,
            country: userDataP.country || "",
            residential_status: userDataP.residential_status || "",
            bank_name: userDataP.bank_name || "",
            branch_name: userDataP.branch_name || "",
            account_number: userDataP.account_number || "",
            account_type: userDataP.account_type || "",
            ifsc_code: userDataP.ifsc_code || "",
            nominee_name: userDataP.nominee_name || "",
            nominee_relationship: userDataP.nominee_relationship || "",
            nominee_dob: userDataP.nominee_dob || "",
            guardian_details: userDataP.guardian_details || "",
        };

        setIsSubmitting(true);
        try {
            const res = await axios.put(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/user/updateUser/${id}`,
                payload
            );
            if (res?.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: "Profile Updated Successfully",
                    timer: 1500,
                }).then(() => {
                    setIsEditing(false);
                    getProfileData();
                });
            }
        } catch (err) {
            console.error("Profile update error:", err);
            Swal.fire({ icon: "error", title: "Update failed" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const statusConfig = getKYCStatusConfig(kycStatus);

    const inputStyle = (isEdit) => ({
        borderRadius: "10px",
        borderColor: "#b3eaf4",
        fontSize: "14px",
        backgroundColor: isEdit ? "#ffffff" : "#f8fafc",
    });

    const inputGroupStyle = {
        backgroundColor: "#ccf1f7",
        borderColor: "#b3eaf4",
        borderRadius: "10px 0 0 10px",
    };

    if (loading) {
        return (
            <>
                <ClientNavbar />
                <div className="page-content" style={{ background: "#f8fafc", minHeight: "100vh" }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                        <div className="spinner-border text-primary" role="status" />
                        <span className="ms-2">Loading profile...</span>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                {/* Header */}
                <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h3 className="text-primary mb-2">Profile & KYC</h3>
                                <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none" }}>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <NavLink to="/overview">Dashboard</NavLink> /
                                        </li>
                                        <li className="breadcrumb-item">
                                            <NavLink to="/account/profile-kyc">Account</NavLink> /
                                        </li>
                                        <li className="breadcrumb-item active">Profile & KYC</li>
                                    </ol>
                                </nav>
                            </div>
                            {!isEditing ? (
                                <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
                                    Edit Profile
                                </button>
                            ) : (
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn fw-semibold px-4"
                                        onClick={() => setIsEditing(false)}
                                        style={{
                                            background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                            color: "white",
                                            border: "none",
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSaveProfile}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Profile Form */}
                    <div className="col-lg-8">
                        <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                            <div
                                className="card-header border-0 py-3 px-4"
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                    borderBottom: "1px solid #b3eaf4",
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <div
                                        className="me-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "10px",
                                            background: "#c8eeecbf",
                                        }}
                                    >
                                        <FiUser style={{ color: "#39b2b4", fontSize: "20px" }} />
                                    </div>
                                    <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>
                                        Personal Information
                                    </h5>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSaveProfile}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                First Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="firstName"
                                                value={profileData.firstName}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                required
                                                style={inputStyle(isEditing)}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Last Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="lastName"
                                                value={profileData.lastName}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                required
                                                style={inputStyle(isEditing)}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Email <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text border-end-0" style={inputGroupStyle}>
                                                    <FiMail style={{ color: "#006e82", fontSize: "18px" }} />
                                                </span>
                                                <input
                                                    type="email"
                                                    className="form-control border-start-0"
                                                    name="email"
                                                    value={profileData.email}
                                                    readOnly
                                                    disabled
                                                    required
                                                    style={{
                                                        ...inputStyle(false),
                                                        borderRadius: "0 10px 10px 0",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Phone <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text border-end-0" style={inputGroupStyle}>
                                                    <FiPhone style={{ color: "#006e82", fontSize: "18px" }} />
                                                </span>
                                                <span
                                                    className="input-group-text"
                                                    style={{
                                                        ...inputGroupStyle,
                                                        borderRadius: 0,
                                                        minWidth: "50px",
                                                        fontSize: "14px",
                                                    }}
                                                >
                                                    {userDataP?.country_code || "+91"}
                                                </span>
                                                <input
                                                    type="tel"
                                                    className="form-control border-start-0"
                                                    value={profileData.phone}
                                                    onChange={handlePhoneChange}
                                                    disabled={!isEditing}
                                                    required
                                                    maxLength={10}
                                                    placeholder="10-digit mobile"
                                                    style={{
                                                        ...inputStyle(isEditing),
                                                        borderRadius: "0 10px 10px 0",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Date of Birth <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text border-end-0" style={inputGroupStyle}>
                                                    <FiCalendar style={{ color: "#006e82", fontSize: "18px" }} />
                                                </span>
                                                <input
                                                    type="date"
                                                    className="form-control border-start-0"
                                                    name="dateOfBirth"
                                                    value={profileData.dateOfBirth}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing}
                                                    required
                                                    style={{
                                                        ...inputStyle(isEditing),
                                                        borderRadius: "0 10px 10px 0",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                PAN Number <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="pan"
                                                value={profileData.pan}
                                                onChange={handleInputChange}
                                                disabled
                                                readOnly
                                                required
                                                maxLength={10}
                                                style={{
                                                    ...inputStyle(false),
                                                    textTransform: "uppercase",
                                                }}
                                            />
                                            <div className="form-text" style={{ fontSize: "12px", color: "#64748b" }}>
                                                Format: ABCDE1234F (read-only)
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Address <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span
                                                    className="input-group-text border-end-0 align-items-start pt-3"
                                                    style={inputGroupStyle}
                                                >
                                                    <FiMapPin style={{ color: "#006e82", fontSize: "18px" }} />
                                                </span>
                                                <textarea
                                                    className="form-control border-start-0"
                                                    name="address"
                                                    value={profileData.address}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing}
                                                    rows="3"
                                                    required
                                                    style={{
                                                        ...inputStyle(isEditing),
                                                        borderRadius: "0 10px 10px 0",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                City <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="city"
                                                value={profileData.city}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                required
                                                style={inputStyle(isEditing)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                State <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="state"
                                                value={profileData.state}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                required
                                                style={inputStyle(isEditing)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                                Pincode <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="pincode"
                                                value={profileData.pincode}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                required
                                                maxLength={6}
                                                style={inputStyle(isEditing)}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* KYC Status */}
                    <div className="col-lg-4">
                        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                            <div
                                className="card-header border-0 py-3 px-4"
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                    borderBottom: "1px solid #b3eaf4",
                                }}
                            >
                                <h6 className="mb-0 fw-bold" style={{ color: "#006e82" }}>KYC Status</h6>
                            </div>
                            <div className="card-body p-4">
                                <div className="text-center mb-4">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center mb-3"
                                        style={{
                                            width: "64px",
                                            height: "64px",
                                            borderRadius: "50%",
                                            backgroundColor: statusConfig.bg,
                                            border: `3px solid ${statusConfig.color}`,
                                        }}
                                    >
                                        <statusConfig.icon style={{ color: statusConfig.color, fontSize: "32px" }} />
                                    </div>
                                    <h5 className="fw-bold mb-2" style={{ color: statusConfig.color }}>
                                        {statusConfig.label}
                                    </h5>
                                    {kycStatus === "rejected" && (
                                        <button
                                            className="btn btn-sm fw-semibold mt-2"
                                            onClick={() => setShowRejectionReason((v) => !v)}
                                            style={{
                                                borderRadius: "8px",
                                                borderColor: "#b3eaf4",
                                                color: "#006e82",
                                                backgroundColor: "transparent",
                                            }}
                                        >
                                            View Rejection Reason
                                        </button>
                                    )}
                                </div>
                                {showRejectionReason && kycStatus === "rejected" && (
                                    <div
                                        className="alert mb-3 border-0"
                                        style={{
                                            borderRadius: "10px",
                                            backgroundColor: "#fee2e2",
                                            border: "1px solid #ef4444",
                                            color: "#991b1b",
                                        }}
                                    >
                                        <strong>Rejection Reason:</strong>
                                        <div className="mt-2" style={{ fontSize: "13px" }}>
                                            {kycRejectionReason}
                                        </div>
                                    </div>
                                )}
                                {(kycStatus === "pending" || kycStatus === "rejected") && (
                                    <button
                                        type="button"
                                        className="btn w-100 fw-semibold"
                                        onClick={handleStartKYC}
                                        disabled={kycLinkLoading}
                                        style={{
                                            borderRadius: "10px",
                                            background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                            color: "white",
                                            border: "none",
                                            padding: "12px",
                                        }}
                                    >
                                        {kycLinkLoading
                                            ? "Fetching link…"
                                            : kycStatus === "rejected"
                                              ? "Restart verification (BSE)"
                                              : "Complete BSE verification"}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* KYC Documents */}
                        <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                            <div
                                className="card-header border-0 py-3 px-4"
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                    borderBottom: "1px solid #b3eaf4",
                                }}
                            >
                                <h6 className="mb-0 fw-bold" style={{ color: "#006e82" }}>KYC Documents</h6>
                            </div>
                            <div className="card-body p-4">
                                {kycDocuments.map((doc) => (
                                    <div
                                        key={doc.id}
                                        className="mb-3 p-3 rounded"
                                        style={{
                                            backgroundColor: doc.status === "uploaded" ? "#ccf1f7" : "#f8fafc",
                                            border: `1px solid ${doc.status === "uploaded" ? "#b3eaf4" : "#e9ecef"}`,
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold" style={{ color: "#006e82", fontSize: "14px" }}>
                                                    {doc.type}
                                                </div>
                                                {doc.status === "uploaded" && doc.file && (
                                                    <div className="d-flex align-items-center mt-1">
                                                        <FiFileText className="me-2" style={{ color: "#39b2b4", fontSize: "14px" }} />
                                                        <span style={{ color: "#64748b", fontSize: "12px" }}>{doc.file}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {doc.status === "uploaded" ? (
                                                <FiCheckCircle style={{ color: "#10b981", fontSize: "18px" }} />
                                            ) : (
                                                <FiAlertCircle style={{ color: "#f59e0b", fontSize: "18px" }} />
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            className="form-control form-control-sm"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => handleFileUpload(e, doc.type)}
                                            style={{
                                                borderRadius: "8px",
                                                borderColor: "#b3eaf4",
                                                fontSize: "12px",
                                            }}
                                        />
                                        {doc.uploadedAt && (
                                            <div className="mt-2" style={{ color: "#64748b", fontSize: "11px" }}>
                                                Uploaded: {formatDate(doc.uploadedAt)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
